import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDrop } from "react-dnd";
import {
  FormCreationData,
  DeviceType,
  SAVE_FORM_JSON,
  UPDATE_PARENT_FIELDS,
} from "../../../../../stateManager/store/formCreation_v2/types";
import { ElementWrapper } from "./ElementWrapper";
import MicroComponents from "./MicroComponents";
import { ItemTypes } from "../types/ItemTypes";
import { onDrop, handleContainers } from "../helpers";
import { getGridStyles as getContainerStyles, getMargin, getPadding } from "../helpers/generateStyles";
import { containers, canvasElements } from "../constants";
import { getContainerField } from "../helpers/assetHelpers";
import { AppState } from "../../../../../stateManager/store";
import { MouseHoverOnTree } from "../../../../../helpers/RxServices";
// icons
import { ReactComponent as UpIcon } from "../../../../../assets/svg/solobuilder/chevron-up.svg";
import { ReactComponent as DownIcon } from "../../../../../assets/svg/solobuilder/chevron-down.svg";
import { useTopElement } from "../hooks/useTopElement";
import { useToast } from "../../../../../helpers/commonFunction";
import { setMultipleSelectionv2 } from "../../../../../stateManager/store/contextMenuv2/action";
import { addWrapperComputedStyles, setSelectedFormItemId, updateElement, updatedSelectedeElements } from "../../../../../stateManager/store/formCreation_v2/actions";
import { clearSelectedItems } from "../../../../../stateManager/store/contextMenu/action";
import { HandleContainersV3 } from "../helpers/HandleLayoutComponentsv3";
import { wizardContentInitialState } from "../helpers/wizardHelpers";
import { store } from "../../../../../stateManager";
import { getRandomId } from "../../../../../utils/random-id";
import classNames from "classnames";
interface IExpansionPanel {
  deviceType: DeviceType;
  element: FormCreationData;
  isCheckedIn?: Boolean;
}
// This component is very similar to Grid with two additinal capabilities.
// One is that you can give it a label and the other is that you can toggle
// the visibility of its children.
export const ExpansionPanel: React.FC<IExpansionPanel> = ({
  element,
  deviceType,
  isCheckedIn
}) => {
  const { selectedElementId, isCoreV3, components } = useSelector((state: any) => state.formJson);
  const { id, fields, isAsset, isChildAsset, type, properties, renderer = false } = element;
  const [isTop, setIsTop] = useTopElement()
  const { WarningToast } = useToast()
  const styles = element?.properties?.styles?.[deviceType] || {};
  const assetEditModeEnabled = useSelector((state: AppState) => state.formJson.assetEditMode.isEnabled)
  const containerStyles = getContainerStyles(styles);
  const paddingStyle = getPadding(styles);
  const elementStyle = properties?.styles || {};
  const marginStyle = getMargin(elementStyle[deviceType])
  const dispatch = useDispatch();
  const { classNameTags = [], upArrow, downArrow } = element?.properties?.config;
  const newStyles = {
    ...element?.properties?.styles?.common,
    ...element?.properties?.styles?.[deviceType]
  }
  const { border, gap, absolute, fixed, top, bottom, left, right, borderRadius, borderColor, color, fontSize, fontFamily = 'inherit', backgroundColor, height, heightMode, width, widthMode, overflowX, overflowY, visible } = newStyles;
  // const { border, borderRadius, borderColor, color, fontSize, fontFamily} = element?.properties?.styles[deviceType] || {};
  const [expanded, setExpanded] = useState(false);
  const visibilityGen = visible ? "grid" : "hidden"
  const combinedClassName = classNameTags.join(" ")
  const [hoveredElId, setHoveredElId] = useState<string>()
  const [isBlur, setIsBlur] = useState(true);
  const editable = !renderer ? true : false;
  const positionGen = absolute ? "absolute" : "relative";

  const defaultColor = "#EFF0F4"
  const handleToggle = () => {
    setExpanded((prevState) => !prevState);
  };
  const iconStyle = {
    width: fontSize,
    height: fontSize,
    stroke: color,
  };
  const headerStyle: React.CSSProperties = {
    ...(fontSize ? { fontSize: fontSize } : {}),
    ...(color ? { color: color } : {}),
    width: "100%",
    height: "auto",
    ...(isBlur ? { overflowX: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", } : {}),
  };
  const nonDroppableAssetTypes = ['FormWizard', 'FlatList'];
  const [{ isOverCurrent, canDrop }, drop] = useDrop({
    accept: [ItemTypes.ELEMENT, ItemTypes.COMPONENT, ItemTypes.ASSET, ItemTypes.TEMPLATE],
    canDrop: (item: any) => {
      // 1. the source container itself gets dropped
      if (id === item.json.id) {
        return false;
      }
      if (item.json.type === 'Spacer' || item.json.type === 'FAB' || canvasElements.includes(item.json.type)) {
        return false
      }
      // 2. the dragged item is already present in the container
      if (item.type === ItemTypes.ELEMENT && item.json.parentId === id) {
        return false;
      }
      // 3. asset instance shouldn't drop source when
      // edit mode is turned off
      if (!assetEditModeEnabled && (isChildAsset || isAsset)) {
        return false
      }

      if (nonDroppableAssetTypes.includes(item?.json?.type) && assetEditModeEnabled) {
        return false
      }
      
      return true;
    },
    collect: (monitor) => {
      return {
        isOverCurrent: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      };
    },
    drop: async (item: any) => {
      if (isOverCurrent) {
        if (isCheckedIn) {
          let fieldId: any
          if (item?.json?.type !== "FormWizard") {
            fieldId = onDrop({ id }, item, deviceType, isCoreV3)
          }
          if (item?.json?.type === "TabBar") {
            let data: any = {
              type: "Component",
              json: {
                type: 'Tab',
                label: 'Tab 1',
                value: "tab 1"
              }
            }
            onDrop({ id: fieldId }, data, deviceType)
          } else if (item?.json?.type === "FormWizard") {
            onDrop({ id }, {type: ItemTypes.FORMWIZARD, json: {}}, deviceType, isCoreV3);
          }
        } else {
          WarningToast("Please Checkout The Form!!")
        }
      }
    },
  });
  const isActive = isOverCurrent && canDrop;
  const animationClass = isActive ? "drop-active" : canDrop ? "drop-passive" : "";
  const isContainerEmpty = fields && fields.length === 0;
  useEffect(() => {
    setIsTop(id)
  }, [id, setIsTop])
  const handleSelect = (e: React.MouseEvent<HTMLDivElement>, id: any) => {
    e.stopPropagation();
    if (e.shiftKey && !isChildAsset) {
      dispatch(setMultipleSelectionv2(true));
      dispatch(updatedSelectedeElements(id));
    } else {
      dispatch(setMultipleSelectionv2(false));
      dispatch(
        setSelectedFormItemId({
          id,
          isAssetInstanceSelected: isAsset || isChildAsset,
        })
      );
      dispatch(clearSelectedItems())
    }
  };

  const handleRightClick = async (e: React.MouseEvent<HTMLDivElement>, id: any) => {
    e.stopPropagation();
    const existingWrapper = document.getElementById("wrapper");
    existingWrapper && await existingWrapper.remove();
    await dispatch(
      setSelectedFormItemId({
        id,
        isAssetInstanceSelected: isAsset || isChildAsset,
      })
    );
    await dispatch(clearSelectedItems());
  };

  const getHeight = (): string => {
    let returnVal = "auto";
    if (heightMode === 'min-content') {
      returnVal = "min-content"
    } else if (heightMode === 'max-content') {
      returnVal = "max-content"
    } else if (heightMode === '%' || heightMode === 'px') {
      returnVal = `${height}${heightMode}`
    }
    return returnVal
  }

  const getWidth = (): string => {
    if (widthMode === 'auto') {
      return "fit-content"
    } else if (widthMode === 'min-content') {
      return "min-content"
    } else if (widthMode === 'max-content') {
      return "max-content"
    } else if (width) {
      return `${width}${newStyles.widthMode}`;
    }
    return ""
  }

  const expansionWrapper = {
    ...(fontFamily ? { fontFamily: fontFamily } : {}),
    height: getHeight(),
    width: getWidth(),
    ...marginStyle,
    top: `${top ? `${top}px` : ""}`,
    left: `${left ? `${left}px` : ""}`,
    bottom: `${bottom ? `${bottom}px` : ""}`,
    right: `${right ? `${right}px` : ""}`,
  }
  const expansionOuterDiv: React.CSSProperties = {
    ...(borderColor ? {borderColor : borderColor }:{}),
    ...(border ? {border : borderColor ? `${border}px solid ${borderColor}` : `${border}px solid ${defaultColor}` }:{}),
    ...(gap ? { gap: `${gap}px` } : {}),
    borderRadius: borderRadius ? `${borderRadius}px` : "8px",
    ...paddingStyle,
    height: getHeight(),
    width: getWidth(),
    backgroundColor: hoveredElId === id ? defaultColor : ""
  }

  const getHeaderHeight = (): string => {
    const headerList = document.getElementsByClassName("expansion-panel__header");
    if (headerList.length) {
      const header = headerList[0];
      const computedStyle = getComputedStyle(header);
      return computedStyle.height
    }
    return ""
  }
  
  const expandedContainerStyle: React.CSSProperties = {
    ...(isContainerEmpty ? { minHeight: 130 } : {}),
    ...(gap ? { gap: `${gap}px` } : {}),
    backgroundColor: backgroundColor || "#fff",
    ...(getHeaderHeight ? { height: `calc(100% - ${getHeaderHeight()})` } : {}),
    overflowY: overflowY || "auto",
    overflowX: overflowX || "hidden",
  }
  useEffect(() => {
    const subscription = MouseHoverOnTree.getMessage().subscribe(function (e) {
      setHoveredElId(e);
    });
    if (!subscription) {
      MouseHoverOnTree.getMessage();
    }
    return () => {
      subscription.unsubscribe();
    };
  }, [hoveredElId]);

  function kebabToCamel(kebab: any) {
    return kebab.replace(/-([a-z])/g, (match: any, letter: any) => letter.toUpperCase());
  }

  useEffect(() => {
    if (selectedElementId === id && (components[selectedElementId]?.type === "ExpansionPanel")) {
      const childElement = document.getElementById(`${type}-${id}`)
      if (childElement) {
        const computedStyles = window.getComputedStyle(childElement);
        const camelCaseStyles: any = {};

        for (let i = 0; i < computedStyles.length; i++) {
          const prop = computedStyles[i];
          const value = computedStyles.getPropertyValue(prop);
          const camelProp = kebabToCamel(prop);
          camelCaseStyles[camelProp] = value;
        }
        const selectedStyles = {
          width: camelCaseStyles.width,
          height: camelCaseStyles.height,
          marginTop: camelCaseStyles.marginTop,
          marginBottom: camelCaseStyles.marginBottom,
          marginLeft: camelCaseStyles.marginLeft,
          marginRight: camelCaseStyles.paddingRight,
          position: camelCaseStyles.position,
          lineHeight: camelCaseStyles.lineHeight,
          backgroundColor: computedStyles.getPropertyValue("background-color"),
          borderColor: computedStyles.getPropertyValue("border-color")
        };
        dispatch(addWrapperComputedStyles(selectedStyles))
      }
    }
  }, [selectedElementId])

  const handleInput = (event: React.FormEvent<HTMLParagraphElement>) => {
    setIsBlur(!isBlur);
    const textContent = event.currentTarget.textContent;
    dispatch(updateElement({
      id: id,
      path: "label",
      value: textContent || ""
    }))
  }

  

  return (
    <>
      {isCoreV3 ?
        <div id={`ExpansionPanel-${id}`} className={`${positionGen} ${visibilityGen}`}  style={expansionWrapper}>
          <div
            ref={(ref) => drop(ref)}
            onClick={(e) => handleSelect(e, id)}
            onContextMenu={(e) => {
              e.preventDefault();
              handleRightClick(e,id);
            }}
            className={`expansion-panel__wrapper ${combinedClassName}`} style={expansionOuterDiv}
            onMouseOver={(e) => {
              e.stopPropagation();
              MouseHoverOnTree.sendMessage(id)
            }}
            onMouseLeave={() => {
              MouseHoverOnTree.sendMessage("")
            }}>
            <div
              className="expansion-panel__header"
              onClick={handleToggle}
              style={{ padding: "0px 12px", height: "auto" }}
            >
              <p style={headerStyle} className="expansion-panel__label" contentEditable={editable} onBlur={handleInput} onFocus={() => setIsBlur(!isBlur)} title={element.label}>{element.label}</p>
              {expanded ? (
                upArrow || upArrow === undefined ? <img src={upArrow} style={iconStyle} className="expansion-panel__svg" /> :
                  <UpIcon style={iconStyle} />
              ) : (
                downArrow || upArrow === undefined ? <img src={downArrow} style={iconStyle} className="expansion-panel__svg" /> :
                  <DownIcon style={iconStyle} />
              )}
            </div>
            {expanded && (
              <div
                style={expandedContainerStyle}
                ref={(ref) => drop(ref)}
                className={`flex flex-col ${animationClass}`}
              >
                {fields.map((fieldId, index) => {
                  const elements = getContainerField(fieldId, element)
                  if (!elements) {
                    return null
                  }
                  const { component, parentComponent } = elements
                  return (
                    <React.Fragment key={fieldId}>
                        {containers.includes(component.type) ? (
                          <HandleContainersV3 isCheckedIn={isCheckedIn} element={component} deviceType={deviceType} index={index} />
                        ) : (
                          <MicroComponents index={index} element={component} deviceType={deviceType} />
                        )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        :
        <div style={{ fontFamily: fontFamily || 'Inherit' }}>
          {selectedElementId === id && type !== "FAB" && type !== "A8Modal" && <p
            data-test-id={id}
            id={`tag_${id}`}
            style={{ ...marginStyle }}
            className={type === "SnackBar" ? "fixed bg-palered text-sm text-white px-1 rounded-sm bottom-14 z-50 left-4 mt-3" : `absolute bg-palered flex items-center text-sm text-white px-1 rounded h-6 bottom-0 z-50 left-0`}>
            {id}</p>
          }
          <div className="space-y-2" id={id} style={{
            borderWidth: border ? `${border}px` : undefined,
            borderRadius: borderRadius ? `${borderRadius}px` : undefined,
            borderColor: borderColor ? `${borderColor}` : undefined,
            ...paddingStyle,
          }}>
            <div
              className="flex items-center justify-between cursor-pointer select-none"
              onClick={handleToggle}
              style={headerStyle}
            >
              <p>{element.label}</p>
              {expanded ? (
                <UpIcon style={iconStyle} />
              ) : (
                <DownIcon style={iconStyle} />
              )}
            </div>
            {expanded && (
              <div
                style={{
                  minHeight: isContainerEmpty ? 130 : 20,
                  gap: styles?.gap ? `${styles?.gap}px` : '0px',
                }}
                ref={(ref) => drop(ref)}
                className={`grid ${containerStyles} ${animationClass}`}
              >
                {fields.map((fieldId, index) => {
                  const elements = getContainerField(fieldId, element)
                  if (!elements) {
                    return null
                  }
                  const { component, parentComponent } = elements
                  return (
                    <>
                      <ElementWrapper key={component.id} isCheckedIn={isCheckedIn} element={component} parentElement={parentComponent} index={index}>
                        <div>
                          {containers.includes(component.type) ? (
                            handleContainers({ isCheckedIn, element: component, deviceType })
                          ) : (
                            <MicroComponents element={component} deviceType={deviceType} />
                          )}</div>
                      </ElementWrapper>
                    </>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      }
    </>
  );
};
